import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';

/**
 * RMS `InfoTooltip`
 * ----------------
 * Why does this file exist when we already have `src/components/InfoTooltip.js`?
 * - This codebase contains multiple "products" (e.g. the ADHD Assessment product and RMS).
 * - Even if the visual design is identical today, these products often diverge over time.
 * - Duplicating the component for RMS avoids accidental cross-product coupling:
 *   changes needed for one product won't silently change the other.
 *
 * UX / behaviour summary (in plain English):
 * - We show a small "?" icon button.
 * - When the user clicks it, we open a small popover with helpful explanatory text.
 * - The popover is rendered via Radix to ensure good accessibility and positioning.
 */
const InfoTooltip = ({
  content,
  label = 'More information',
  side = 'top',
  align = 'center',
  maxWidth = '250px',
  className = '',
}) => {
  /**
   * We support both strings and React nodes for `content`.
   * If the caller passes a string, we split on blank lines so content can be authored
   * in a readable way (e.g. two paragraphs separated by `\n\n`).
   */
  const renderContent = () => {
    if (typeof content !== 'string') return content;

    const paragraphs = content.split('\n\n');
    return paragraphs.map((paragraph, index) => (
      <p key={index} className={index < paragraphs.length - 1 ? 'mb-2' : ''}>
        {paragraph}
      </p>
    ));
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={`text-primary rounded-full hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${className}`}
          aria-label={label}
        >
          <QuestionMarkCircleIcon className="w-4 h-4" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="bg-slate-900 font-semibold text-white rounded-lg p-3 text-sm shadow-lg mx-4 z-[100]"
          style={{ maxWidth }}
          sideOffset={5}
          side={side}
          align={align}
          collisionPadding={16}
        >
          <div>{renderContent()}</div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default InfoTooltip;

